'use client'

import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { useRouter } from 'next/router'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'

import { ResolvedAvatarImage } from '@/components/common/resolved-media-image'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { CommandDialog, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'

import userService from '@/services/user.service'

import { SearchUserItem } from '@/types/search.type'

interface SearchBarCommandProps extends React.HTMLAttributes<HTMLButtonElement> {
  className?: string
}

export default function SearchBarCommand({ className, ...props }: SearchBarCommandProps) {
  const router = useRouter()
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')

  const debouncedInputValue = useDebounce(inputValue, 500)

  const {
    data: users = [],
    isLoading,
    isError,
  } = useQuery<SearchUserItem[]>({
    queryKey: ['search-users', debouncedInputValue],
    queryFn: () => userService.searchUsers({ query: debouncedInputValue }),
    enabled: !!debouncedInputValue,
    staleTime: 60_000, // cache for 1 min
    select: raw => raw.filter((u, i, arr) => i === arr.findIndex(t => t.id === u.id)),
  })

  const handleValueChange = (val: string) => setInputValue(val.trimStart()) // keep leading spaces out

  /* ───────────────────────── hotkey: ⌘/Ctrl-K or “/”───────────────────────── */
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || e.key === '/') {
        const target = e.target as HTMLElement
        if (
          target.isContentEditable ||
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement
        )
          return
        e.preventDefault()
        setOpen(o => !o)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])
  /* ────────────────────────────────────────────────────────────────────────── */

  const runCommand = React.useCallback((fn: () => unknown) => {
    setOpen(false)
    fn()
  }, [])

  return (
    <>
      <button
        className={cn(
          `relative w-full text-base hover:bg-gray-100 hover:text-gray-600 transition-colors duration-200 p-1 rounded-full`,
          className
        )}
        onClick={() => setOpen(true)}
        {...props}
      >
        <Search className="size-6" />
        {/* <span className="hidden lg:inline-flex">Search users...</span>
        <span className="inline-flex lg:hidden">Search...</span> */}
        {/* <kbd className="pointer-events-none absolute right-[0.3rem] top-[0.5rem] hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd> */}
      </button>

      {/* Custom positioning wrapper */}
      <div className="fixed inset-0 z-50 pointer-events-none" style={{ display: open ? 'block' : 'none' }}>
        <div className="absolute top-24 left-1/2 transform -translate-x-1/2 w-full max-w-2xl mx-auto pointer-events-auto">
          <CommandDialog open={open} onOpenChange={setOpen} commandProps={{ shouldFilter: false }}>
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
              <div className="border-b-0">
                <CommandInput
                  placeholder="Search for users..."
                  onValueChange={handleValueChange}
                  className="h-12 text-base outline-none border-none focus:ring-0 focus:border-transparent focus:outline-none"
                />
              </div>
              <CommandList className="max-h-[80vh] overflow-hidden overflow-y-auto pt-2">
                {isLoading ? (
                  <CommandGroup heading="Searching...">
                    <ScrollArea className="h-[min(70vh,500px)] w-full">
                      {[...Array(3)].map((_, index) => (
                        <div key={index} className="py-3 px-4">
                          <div className="flex items-center gap-4">
                            <Skeleton className="flex-shrink-0 size-12 rounded-full" />
                            <div className="min-w-0 flex-1 space-y-2">
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-3 w-48" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                  </CommandGroup>
                ) : users?.length ? (
                  <CommandGroup heading={`Users (${users.length})`}>
                    <ScrollArea className="h-[min(70vh,500px)] w-full">
                      {users?.map(user => {
                        return (
                          <CommandItem
                            key={user.id}
                            value={`${user.id}`}
                            onSelect={() => {
                              runCommand(() => router.push(`/user/${user.profileId}`))
                            }}
                            className="group overflow-hidden py-3 px-4 cursor-pointer transition-all duration-200 hover:bg-blue-50 data-[selected='true']:bg-blue-100 data-[selected=true]:text-blue-900 focus:bg-blue-50 active:bg-blue-200"
                          >
                            <div className="flex items-center gap-4">
                              <div className="flex-shrink-0 size-12 overflow-hidden flex items-center justify-center">
                                <Avatar>
                                  <ResolvedAvatarImage avatar={user.avatar} alt={`${user.firstName} ${user.lastName}`} className="object-cover rounded-full" />
                                  <AvatarFallback className="bg-blue-100 text-blue-600 transition-colors duration-200 group-hover:bg-blue-200 group-data-[selected='true']:bg-blue-200">
                                    {user.firstName.charAt(0)}
                                    {user.lastName.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-base font-medium text-gray-900 truncate transition-colors duration-200 group-hover:text-blue-900 group-data-[selected='true']:text-blue-900">
                                  {user.firstName} {user.lastName}
                                </p>
                                <p className="text-sm text-gray-500 truncate transition-colors duration-200 group-hover:text-blue-700 group-data-[selected='true']:text-blue-700">
                                  {user.profileId}
                                </p>
                              </div>
                            </div>
                          </CommandItem>
                        )
                      })}
                    </ScrollArea>
                  </CommandGroup>
                ) : inputValue ? (
                  <div className="py-6 px-4 text-center h-[min(70vh,500px)]">
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                    <p className="mt-1 text-sm text-gray-500 text-center w-full">Try adjusting your search terms.</p>
                  </div>
                ) : (
                  <div className="py-6 px-4 text-center h-[min(70vh,500px)]">
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Start searching</h3>
                    <p className="mt-1 text-sm text-gray-500 text-center w-full">Type to search for users by name.</p>
                  </div>
                )}
                {isError && (
                  <div className="py-6 px-4 text-center h-[min(70vh,500px)]">
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
                    <p className="mt-1 text-sm text-gray-500 text-center w-full">Try again later.</p>
                  </div>
                )}
              </CommandList>
            </div>
          </CommandDialog>
        </div>
      </div>
    </>
  )
}
